import styled from 'styled-components';

// ---------------------------------------------------------------------------
// RH · Recrutamento — estilos ESPECÍFICOS da ficha do candidato: a esteira
// (timeline) e o bloco de currículo. Os primitivos de detalhe compartilhados
// (Card, Fact, Header, BackRow…) vivem em ../detailStyles e são re-exportados
// aqui para não mudar os imports dos consumidores desta tela.
// ---------------------------------------------------------------------------

export {
   HeaderBar,
   HeaderMain,
   Avatar,
   TitleRow,
   MetaLine,
   Card,
   CardHead,
   FactGrid,
   Fact,
   InlineHint,
   BackRow,
} from '../detailStyles';

const border = ({ theme }) => theme?.colors?.border ?? 'rgba(0,0,0,0.10)';
const muted = ({ theme }) => theme?.colors?.textMuted ?? '#6b7280';
const ink = ({ theme }) => theme?.colors?.textPrimary ?? '#1f1f18';

// ─── Identidade & contato ────────────────────────────────────────────────────
// Grade FIXA de 5 colunas (mock .idc) — o FactGrid compartilhado (auto-fit)
// fica intocado pras outras telas.
export const IdentityGrid = styled.div`
   display: grid;
   grid-template-columns: repeat(5, 1fr);
   gap: 18px 22px;

   @media (max-width: 1200px) {
      grid-template-columns: repeat(3, 1fr);
   }
   @media (max-width: 720px) {
      grid-template-columns: repeat(2, 1fr);
   }
`;

// Observações do candidato — bloco de rodapé do card (mock .idc-obs).
export const ObsBlock = styled.div`
   margin-top: 20px;
   padding-top: 16px;
   border-top: 1px solid ${border};
   display: flex;
   flex-direction: column;
   gap: 6px;

   .obs-label {
      font-size: 10px;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      font-weight: 700;
      color: ${muted};
   }
   .obs-value {
      font-size: 0.9rem;
      font-weight: 500;
      color: ${ink};
      white-space: pre-wrap;
   }
`;

// ─── Split onboarding × esteira (mock .split) ────────────────────────────────
export const Split = styled.div`
   display: grid;
   grid-template-columns: 1fr 1fr;
   gap: 16px;
   align-items: start;

   @media (max-width: 940px) {
      grid-template-columns: 1fr;
   }
`;

// Linha vertical do card de onboarding (mock .ob-item): label uppercase +
// valor/pill, com divisor entre linhas.
export const ObItem = styled.div`
   padding: 14px 0;
   border-bottom: 1px solid ${border};

   &:first-child {
      padding-top: 2px;
   }
   &:last-child {
      border-bottom: none;
      padding-bottom: 2px;
   }

   .ob-label {
      font-size: 10px;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      font-weight: 700;
      color: ${muted};
      margin-bottom: 8px;
   }
   .ob-value {
      font-size: 15px;
      font-weight: 600;
      color: ${ink};
   }
   .ob-value.empty {
      color: #c2c2c2;
      font-weight: 500;
   }
`;

// ─── Currículo compacto (dentro do card Identidade & contato) ───────────────
// Linha inline do ResumeFact: hyperlink à esquerda, botão de substituir na
// ponta direita do container. O ícone gira durante o upload.
export const ResumeFactRow = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 10px;

   /* Texto estático + ícone de abrir colados; substituir fica na ponta. */
   .resume-view {
      display: inline-flex;
      align-items: center;
      gap: 2px;
   }

   .spin {
      animation: resume-spin 0.9s linear infinite;
   }
   @keyframes resume-spin {
      to {
         transform: rotate(360deg);
      }
   }
`;

// Fato inline no CardHead (ex.: "Vaga atual" na linha do título do card).
export const HeadFact = styled.div`
   display: inline-flex;
   align-items: center;
   gap: 8px;
   flex-wrap: wrap;
   font-size: 0.86rem;

   .head-fact-label {
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.03em;
      color: ${muted};
   }
   strong {
      font-weight: 700;
      color: ${ink};
   }
`;

// ─── Esteira / timeline vertical por RODADA (mock .tl*) ─────────────────────
// Estados do passo: ok (verde) · wait (neutro) · reproved (vermelho, com badge
// ✕ bloqueando o conector pro passo seguinte).
const stepPalette = {
   ok: {
      node: 'rgba(46,125,50,0.12)',
      nodeBorder: 'rgba(46,125,50,0.55)',
      nodeText: '#1b5e20',
      connector: 'rgba(46,125,50,0.5)',
      cardBorder: 'rgba(46,125,50,0.4)',
      cardBg: 'rgba(46,125,50,0.03)',
   },
   reproved: {
      node: 'rgba(211,47,47,0.1)',
      nodeBorder: 'rgba(211,47,47,0.5)',
      nodeText: '#b71c1c',
      connector: 'rgba(211,47,47,0.45)',
      cardBorder: 'rgba(211,47,47,0.35)',
      cardBg: 'rgba(211,47,47,0.03)',
   },
   wait: {
      node: '#f3f4f6',
      nodeBorder: '#d7dade',
      nodeText: '#9aa0a6',
      connector: '#e2e4e7',
      cardBorder: 'rgba(0,0,0,0.10)',
      cardBg: '#fff',
   },
};
const step = (key) => ({ $state }) =>
   (stepPalette[$state] ?? stepPalette.wait)[key];

export const Tl = styled.div`
   position: relative;
`;

export const TlStep = styled.div`
   position: relative;
   display: grid;
   grid-template-columns: 40px 1fr;
   gap: 14px;
   padding-bottom: 20px;

   &::before {
      content: '';
      position: absolute;
      left: 19px;
      top: 40px;
      bottom: -2px;
      width: 2px;
      background: ${step('connector')};
      z-index: 0;
   }
   &:last-child {
      padding-bottom: 0;
   }
   &:last-child::before {
      display: none;
   }
`;

export const TlNode = styled.div`
   position: relative;
   z-index: 1;
   width: 40px;
   height: 40px;
   border-radius: 999px;
   display: grid;
   place-items: center;
   font-size: 14px;
   font-weight: 800;
   background: ${step('node')};
   border: 2px solid ${step('nodeBorder')};
   color: ${step('nodeText')};
`;

// Badge ✕ sobre o conector do passo reprovado (bloqueia o seguinte).
export const TlX = styled.div`
   position: absolute;
   left: 11px;
   top: 48px;
   z-index: 2;
   width: 18px;
   height: 18px;
   border-radius: 999px;
   background: #fff;
   border: 1.5px solid rgba(211, 47, 47, 0.6);
   color: #b71c1c;
   display: grid;
   place-items: center;
`;

export const TlCard = styled.div`
   border: 1px solid ${step('cardBorder')};
   border-radius: 10px;
   padding: 12px 14px;
   background: ${step('cardBg')};
`;

export const TlTitle = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
   row-gap: 6px;
   flex-wrap: wrap;

   h3 {
      font-size: 15px;
      font-weight: 700;
      margin: 0;
      color: ${ink};
      white-space: nowrap;
   }
   .tl-mode {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-size: 12px;
      color: ${muted};
      font-weight: 600;
   }
   .tl-pill {
      margin-left: auto;
   }
`;

export const TlMeta = styled.div`
   margin-top: 8px;
   display: flex;
   flex-direction: column;
   gap: 4px;
   font-size: 12.5px;
   line-height: 1.4;
   color: ${muted};

   strong {
      color: ${ink};
      font-weight: 600;
   }
`;

export const TlEmpty = styled.div`
   margin-top: 6px;
   font-size: 12.5px;
   color: #b0b0b0;
   font-style: italic;
`;

// Remarcações anteriores (expand compacto dentro do card da rodada).
export const TlHistory = styled.div`
   margin-top: 10px;
   padding-top: 10px;
   border-top: 1px dashed ${border};
   display: flex;
   flex-direction: column;
   gap: 4px;
   font-size: 12px;
   color: ${muted};
`;

export const TlActions = styled.div`
   margin-top: 12px;
   display: flex;
   gap: 8px;
   flex-wrap: wrap;
`;

export const TlLegend = styled.div`
   display: flex;
   flex-wrap: wrap;
   gap: 14px;
   margin-top: 16px;
   padding-top: 14px;
   border-top: 1px dashed ${border};

   .lg {
      display: flex;
      align-items: center;
      gap: 7px;
      font-size: 12px;
      color: ${muted};
   }
   .swatch {
      width: 12px;
      height: 12px;
      border-radius: 999px;
      border: 2px solid;
   }
   .swatch.ok {
      background: rgba(46, 125, 50, 0.15);
      border-color: rgba(46, 125, 50, 0.55);
   }
   .swatch.wait {
      background: #f3f4f6;
      border-color: #d7dade;
   }
   .swatch.bad {
      background: rgba(211, 47, 47, 0.12);
      border-color: rgba(211, 47, 47, 0.55);
   }
`;

export const NotesBlock = styled.div`
   margin-top: 12px;
   padding-top: 12px;
   border-top: 1px dashed ${border};
   display: grid;
   gap: 8px;

   .note-label {
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.03em;
      color: ${muted};
   }
   .note-value {
      font-size: 0.86rem;
      color: ${ink};
      white-space: pre-wrap;
      word-break: break-word;
   }
`;
